import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import { AutoMatch, toAutoMatches } from './useBulkAutoMatch';
import { useGetLimitedPartnerAutoMatchSuggestionsLazyQueryGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { errorToastConfig } from '~/packages/common-frontend/constants';

/**
 * Outstanding auto-matches keyed by credit facility id. Only facilities that
 * had at least one suggestion when loaded are keys; a facility stays a key
 * (with an empty list) once all its matches are applied, so it keeps showing
 * as done rather than dropping out of a "ready to auto-match" view.
 */
export type AutoMatchesByFacility = ReadonlyMap<string, AutoMatch[]>;

/**
 * Org-wide auto-match suggestions, loaded on demand: they cover every
 * facility's mappings, which is heavy for large orgs.
 */
export const useAutoMatchSuggestions = (
  lenderOrganizationId: string,
): {
  autoMatchesByFacility: AutoMatchesByFacility | null;
  loadSuggestions: () => Promise<AutoMatchesByFacility | null>;
  isLoading: boolean;
  markMatched: (
    match: Pick<AutoMatch, 'creditFacilityId' | 'mappingId'>,
  ) => void;
} => {
  const toast = useToast();
  const [
    autoMatchesByFacility,
    setAutoMatchesByFacility,
  ] = useState<AutoMatchesByFacility | null>(null);

  const [
    fetchSuggestions,
    { loading: isLoading },
  ] = useGetLimitedPartnerAutoMatchSuggestionsLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  const loadSuggestions = useCallback(async (): Promise<AutoMatchesByFacility | null> => {
    const { data, error } = await fetchSuggestions({
      variables: { lenderOrganizationId },
    });
    if (error || !data) {
      toast({
        ...errorToastConfig,
        title: error?.message ?? 'Failed to load auto-match suggestions.',
      });
      return null;
    }
    const loaded = new Map<string, AutoMatch[]>();
    data.limitedPartnerMappingsByFacility.forEach((facility) => {
      const matches = toAutoMatches(facility.mappings);
      if (matches.length > 0) {
        loaded.set(facility.creditFacilityId, matches);
      }
    });
    setAutoMatchesByFacility(loaded);
    return loaded;
  }, [fetchSuggestions, lenderOrganizationId, toast]);

  // Drop a mapping once it's matched (by a bulk run or by hand), so counts
  // stay current without reloading every facility's suggestions.
  const markMatched = useCallback(
    ({
      creditFacilityId,
      mappingId,
    }: Pick<AutoMatch, 'creditFacilityId' | 'mappingId'>): void => {
      setAutoMatchesByFacility((previous) => {
        const matches = previous?.get(creditFacilityId);
        if (!previous || !matches?.some((m) => m.mappingId === mappingId)) {
          return previous;
        }
        const next = new Map(previous);
        next.set(
          creditFacilityId,
          matches.filter((m) => m.mappingId !== mappingId),
        );
        return next;
      });
    },
    [],
  );

  return { autoMatchesByFacility, loadSuggestions, isLoading, markMatched };
};
